import type {IframeLayoutMarginParams} from '#/helpers/iframe/layout';
import type {ConversationEvent} from '#/models/companionEvent';
import type {LayoutPresetsEnum} from '#/models/frame';
import {HomeModesEnum, decodeHomeMode} from '#/models/home';
import {LauncherModesEnum, decodeLauncherMode} from '#/models/launcher';

/*
 * Types.
 */

export interface CompanionScriptAutoInitParams extends Pick<
  IframeLayoutMarginParams,
  'sideMargin' | 'bottomMargin'
> {
  container: HTMLElement;
  companionToken: string;
  userJwt: string | undefined;
  layout: LayoutPresetsEnum | undefined;
  launcherMode: LauncherModesEnum;
  homeMode: HomeModesEnum;
  // When set via `data-disabled="true"`, the widget is gated behind a cookie-consent banner: it is
  // initialized but not booted (no iframe, no network, no cookies) until `enable(true)` is called.
  disabled: boolean;
  demo: boolean | undefined;
  conversationId: string | undefined;
  accessToken: string | undefined;
  onConversationChange?: (conversationId: string | undefined) => void;
  onConversationEvent?: (event: ConversationEvent) => void;
}

type ScriptCallback<TArgs extends unknown[]> = (...args: TArgs) => void;

/*
 * Helpers.
 */

/**
 * Extracts auto-init parameters from `#front-companion-script` when `data-auto-init` is set to `true`.
 *
 * Returns `undefined` if `#front-companion-script` is missing (logs a warning), or when `data-auto-init` is not set to `true` or
 * required attributes are missing.
 */
export function extractCompanionScriptAutoInitParams(): CompanionScriptAutoInitParams | undefined {
  const companionScript = queryCompanionScriptElement();
  if (!companionScript) {
    console.warn('#front-companion-script not found');
    return undefined;
  }

  const shouldAutoInit = parseBooleanAttribute(companionScript, 'data-auto-init');
  const scriptCompanionToken = parseStringAttribute(companionScript, 'data-token');
  const scriptContainerId = parseStringAttribute(companionScript, 'data-container-id');
  const scriptContainer = scriptContainerId
    ? document.querySelector<HTMLElement>(`#${scriptContainerId}`)
    : undefined;

  if (!shouldAutoInit || !scriptCompanionToken || !scriptContainer) {
    return undefined;
  }

  const layout = parseStringAttribute(companionScript, 'data-layout') as LayoutPresetsEnum | undefined;
  const disabled = parseBooleanAttribute(companionScript, 'data-disabled');
  const demo = parseBooleanAttribute(companionScript, 'data-demo');
  const conversationId = parseStringAttribute(companionScript, 'data-conversation-id');
  const accessToken = parseStringAttribute(companionScript, 'data-access-token');
  const userJwt = parseStringAttribute(companionScript, 'data-user-jwt');
  const sideMargin = parseStringAttribute(companionScript, 'data-side-margin');
  const bottomMargin = parseStringAttribute(companionScript, 'data-bottom-margin');
  let launcherMode = LauncherModesEnum.DEFAULT;
  const launcherAttribute = parseStringAttribute(companionScript, 'data-launcher');
  if (launcherAttribute) {
    try {
      launcherMode = decodeLauncherMode(launcherAttribute);
    } catch (error) {
      console.warn('FrontCompanion auto-init: invalid data-launcher', error);
    }
  }
  let homeMode = HomeModesEnum.DEFAULT;
  const homeAttribute = parseStringAttribute(companionScript, 'data-home');
  if (homeAttribute) {
    try {
      homeMode = decodeHomeMode(homeAttribute);
    } catch (error) {
      console.warn('FrontCompanion auto-init: invalid data-home', error);
    }
  }
  const onConversationChange = parseWindowCallbackFromAttribute<[conversationId: string | undefined]>(
    companionScript,
    'data-on-conversation-change',
  );
  const onConversationEvent = parseWindowCallbackFromAttribute<[event: ConversationEvent]>(
    companionScript,
    'data-on-conversation-event',
  );

  return {
    container: scriptContainer,
    companionToken: scriptCompanionToken,
    userJwt,
    layout,
    sideMargin,
    bottomMargin,
    launcherMode,
    homeMode,
    disabled,
    demo,
    conversationId,
    accessToken,
    onConversationChange,
    onConversationEvent,
  };
}

function queryCompanionScriptElement() {
  return document.querySelector<HTMLScriptElement>('#front-companion-script');
}

function parseBooleanAttribute(script: HTMLScriptElement, attribute: string): boolean {
  const value = script.getAttribute(attribute);
  if (value === null || value === undefined) {
    return false;
  }

  return value === 'true' || value === '';
}

function parseStringAttribute(script: HTMLScriptElement, attribute: string): string | undefined {
  const value = script.getAttribute(attribute);
  return value ? value : undefined;
}

/**
 * Reads a `data-on-*` attribute as a string, then resolves it to a global function. The value is expected
 * to be the name of a function defined on `window` by the host page.
 */
function parseWindowCallbackFromAttribute<TArgs extends unknown[]>(
  script: HTMLScriptElement,
  attribute: string,
): ScriptCallback<TArgs> | undefined {
  const fnName = parseStringAttribute(script, attribute);
  if (!fnName) {
    return undefined;
  }
  const fn = Object.getOwnPropertyDescriptor(window, fnName)?.value;
  return typeof fn === 'function' ? (fn as ScriptCallback<TArgs>) : undefined;
}
